import { useQuery } from '@tanstack/react-query';

import { useSummarizationId } from 'hooks/useSummarizationId';
import { attachmentsQueryKeys, showAttachment } from 'services/api/attachments';

/**
 * Low-level signed-URL fetch for an attachment, shared by the click-to-open
 * (`useClickAttachment`) and inline-preview (`useAttachmentUrl`) hooks.
 *
 * Pass `enabled: false` to defer the request until the caller is ready (e.g.,
 * first hover, first click). The URL is cached for ~30s by attachment id.
 */
export function useAttachmentSignedUrl(
  attachmentId: string,
  attachableModel?: string,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const summarizationId = useSummarizationId();
  const resolvedAttachableModel = attachableModel ?? (summarizationId ? 'summarization' : undefined);

  return useQuery({
    queryKey: attachmentsQueryKeys.show(attachmentId),
    queryFn: () =>
      showAttachment({
        id: attachmentId,
        include: 'url',
        viewOnly: true,
        ...(resolvedAttachableModel ? { attachableModel: resolvedAttachableModel } : {}),
      }),
    enabled: enabled && !!attachmentId,
    staleTime: 29 * 1000,
  });
}
